import GrayCard from "components/gray-card";
import DashboardLayout from "components/layouts/dashboard-layout";
import PageLinks from "components/page-links";
import ProgressSteps from "components/progress-steps";
import StepsNav from "components/steps-nav";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import StepOne from "./components/steps/stepOne";
import StepTwo from "./components/steps/stepTwo";
import {
  IPromoDataType,
  PromotionType,
  SelectedPackage
} from "types/promoType";
import StepThree from "./components/steps/stepThree";
import StepFour from "./components/steps/stepFour";

const DashboardPromoType = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [finish, setFinish] = useState(false);

  const [data, setData] = useState<IPromoDataType>({
    types: []
  });

  const ToggleTypes = (type: PromotionType) => {
    const oldTypes = data.types;

    if (oldTypes.includes(type)) {
      const newTypes = oldTypes.filter((value) => value !== type);

      setData({ ...data, types: newTypes });
    } else {
      const newTypes = [...oldTypes, type];
      setData({ ...data, types: newTypes });
    }
  };

  const SetMedia = (key: PromotionType, value: any) => {
    setData({ ...data, media: { ...data.media, [key]: value } });
  };

  const SetCartItem = (item: SelectedPackage) => {
    setData({ ...data, cart: [...(data?.cart || []), item] });
  };

  const RemoveCartItem = (item: SelectedPackage) => {
    setData({
      ...data,
      cart: (data?.cart || []).filter((cartItem) => cartItem !== item)
    });
  };

  const ClearCart = () => {
    setData({ ...data, cart: [] });
  };

  const getNavigationState = () => {
    let canNext = false;
    let canPrevious = currentStep > 1;

    if (currentStep === 1 && data.types.length > 0) {
      canNext = true;
    }

    if (currentStep === 2) {
      // Check if every selected type has a corresponding value in media
      canNext = data.types.every((type) => {
        const mediaValue = data.media?.[type];

        if (type === "Blog/Press Release") {
          // For Blog/Press Release, check if it has the required structure
          // media is required, note and file are optional
          return (
            mediaValue &&
            typeof mediaValue === "object" &&
            "media" in mediaValue
          );
        } else {
          // For Radio Promotion and TV Promotion, check if it's a string
          return typeof mediaValue === "string" && mediaValue.trim() !== "";
        }
      });
    }

    if (currentStep === 3 && data.cart && data.cart.length > 0) {
      canNext = true;
    }

    return { canNext, canPrevious };
  };

  const { canNext, canPrevious } = useMemo(getNavigationState, [
    currentStep,
    data
  ]);

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    } else {
      setFinish(true);
    }
  };

  useEffect(() => {
    if (data.cart && data.cart.length === 0 && currentStep === 4) {
      setCurrentStep(3);
    }
  }, [data, currentStep]);

  return (
    <DashboardLayout>
      <>
        <PageLinks
          from={{ name: "Promotion", link: "/promotion" }}
          current="PromoType"
        />
        <ProgressSteps
          currentStep={currentStep}
          steps={["Promo Type", "Media Type", "Platforms", "Checkout"]}
        />
        <Body>
          {currentStep === 1 ? (
            <StepOne
              types={data.types}
              toggleTypes={(type) => ToggleTypes(type)}
            />
          ) : currentStep === 2 ? (
            <StepTwo
              types={data.types}
              setMedia={(key, value) => SetMedia(key, value)}
              media={data.media}
            />
          ) : currentStep === 3 ? (
            <StepThree
              types={data.types}
              cartItems={data.cart || []}
              setCartItems={SetCartItem}
              setStep={(step: number) => setCurrentStep(step)}
            />
          ) : currentStep === 4 ? (
            <StepFour
              cartItems={data.cart || []}
              removeCartItem={(pkg) => RemoveCartItem(pkg)}
              clearCart={() => ClearCart()}
            />
          ) : (
            <></>
          )}
        </Body>
        {currentStep > 0 && (
          <StepsNav
            steps={4}
            currentStep={currentStep}
            canNext={canNext}
            canPrevious={canPrevious}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        )}
      </>
    </DashboardLayout>
  );
};

export default DashboardPromoType;

const Body = styled.div`
  width: 100%;
  padding-top: 2.4rem;
  padding-bottom: 6.4rem;
  min-height: 67dvh;
`;
